import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
   FiGrid,
   FiClipboard,
   FiShield,
   FiZap,
   FiLayers,
   FiBarChart2,
   FiArrowRight,
   FiPlay,
   FiLock,
   FiCheck,
} from 'react-icons/fi';
import { pickLang, PROCESS_STATUS } from '../../data/playbookStory';

const ICONS = {
   grid: FiGrid,
   clipboard: FiClipboard,
   shield: FiShield,
   zap: FiZap,
   layers: FiLayers,
   chart: FiBarChart2,
};

export const StageIcon = ({ name, size = 18 }) => {
   const Icon = ICONS[name] || FiGrid;
   return <Icon size={size} aria-hidden="true" />;
};

const money = (price, locale) => {
   if (!price) return null;
   try {
      return new Intl.NumberFormat(locale, {
         style: 'currency',
         currency: price.currency || 'EUR',
         maximumFractionDigits: 0,
      }).format(price.amount);
   } catch (e) {
      return `${price.amount} ${price.currency || 'EUR'}`;
   }
};

/**
 * Rejilla de procesos + panel de detalle COMPARTIDO.
 *
 * El detalle no se despliega dentro de cada tarjeta a propósito: al hacerlo, la
 * tarjeta abierta crecía tanto que se salía de la escena fija y quedaba cortada
 * (medido en captura). Con un panel de altura constante debajo, la escena no
 * cambia de tamaño al pasar de un proceso a otro y no hay saltos de layout.
 *
 * El proceso activo se elige al pasar el ratón, al enfocar con el teclado o al
 * pulsar; nunca DEPENDE del hover. Arranca con el primero abierto para que la
 * etapa se entienda sin tocar nada.
 */
const ProcessGrid = ({ processes, lang, locale, estimateNotice }) => {
   const { t } = useTranslation();
   const [activeKey, setActiveKey] = useState(processes[0]?.key);
   const active = processes.find((p) => p.key === activeKey) || processes[0];
   const price = money(active?.suggestedPrice, locale);

   return (
      <div className="pbs-procs">
         <div className="pbs-procs__grid">
            {processes.map((process) => {
               const roadmap = process.status === PROCESS_STATUS.ROADMAP;
               const isActive = process.key === active?.key;
               return (
                  <button
                     type="button"
                     key={process.key}
                     className={`pbs-proc${isActive ? ' is-active' : ''}${
                        roadmap ? ' is-roadmap' : ''
                     }`}
                     onPointerEnter={(e) => {
                        if (e.pointerType && e.pointerType !== 'mouse') return;
                        setActiveKey(process.key);
                     }}
                     onFocus={() => setActiveKey(process.key)}
                     onClick={() => setActiveKey(process.key)}
                     aria-pressed={isActive}
                  >
                     <span className="pbs-proc__engine">{pickLang(process.engine, lang)}</span>
                     <span className="pbs-proc__title">{pickLang(process.title, lang)}</span>
                     {roadmap ? (
                        <span className="pbs-proc__badge">{t('pbs_roadmap')}</span>
                     ) : (
                        <span className="pbs-proc__badge pbs-proc__badge--live">
                           <FiCheck size={11} aria-hidden="true" /> {t('pbs_live')}
                        </span>
                     )}
                  </button>
               );
            })}
         </div>

         {active ? (
            // aria-live: al cambiar de proceso con el teclado, el lector de
            // pantalla anuncia el detalle nuevo sin tener que ir a buscarlo.
            <div className="pbs-detail" aria-live="polite">
               <dl>
                  <div>
                     <dt>{t('pbs_problem')}</dt>
                     <dd>{pickLang(active.problem, lang)}</dd>
                  </div>
                  <div>
                     <dt>{t('pbs_result')}</dt>
                     <dd>{pickLang(active.result, lang)}</dd>
                  </div>
                  <div>
                     <dt>{t('pbs_automates')}</dt>
                     <dd>{pickLang(active.automates, lang)}</dd>
                  </div>
                  <div>
                     <dt>{price ? t('pbs_price') : t('pbs_impact')}</dt>
                     <dd>
                        {price ? (
                           <>
                              {price}
                              <em>/{t('pbs_month')}</em>
                           </>
                        ) : (
                           pickLang(active.impact, lang) || '—'
                        )}
                     </dd>
                  </div>
               </dl>
               {active.isEstimate ? (
                  <p className="pbs-detail__estimate">{pickLang(estimateNotice, lang)}</p>
               ) : null}
            </div>
         ) : null}
      </div>
   );
};

/**
 * Escena de UNA etapa. Todas comparten marco (cabecera + titular) y cambia el
 * cuerpo, para que el recorrido se lea como un mismo sistema que se va
 * activando, no como seis secciones distintas apiladas.
 */
const PlaybookStage = ({ stage, index, total, lang, estimateNotice, onOpenDemo }) => {
   const { t, i18n } = useTranslation();
   const locale = i18n?.language === 'en' ? 'en-US' : 'es-ES';

   return (
      <div className="pbs-stage">
         <div className="pbs-stage__head">
            <span className="pbs-stage__badge">
               <StageIcon name={stage.icon} />
               {t('pbs_stage_of', { current: index + 1, total })}
            </span>
            <h3 className="pbs-stage__name">{pickLang(stage.name, lang)}</h3>
            <p className="pbs-stage__headline">{pickLang(stage.headline, lang)}</p>
         </div>

         <div className="pbs-stage__body">
            {/* 1 — Elegir qué vender */}
            {stage.options ? (
               <div className="pbs-options">
                  {stage.options.map((option) => (
                     <Link
                        to={option.to}
                        key={option.key}
                        className={`pbs-option${option.primary ? ' is-primary' : ''}`}
                     >
                        {option.primary ? (
                           <span className="pbs-option__tag">{t('pbs_recommended')}</span>
                        ) : null}
                        <strong>{pickLang(option.title, lang)}</strong>
                        <span>{pickLang(option.text, lang)}</span>
                        <span className="pbs-option__go">
                           {t('pbs_see')} <FiArrowRight size={13} aria-hidden="true" />
                        </span>
                     </Link>
                  ))}
               </div>
            ) : null}

            {/* 2 — Onboarding */}
            {stage.collects ? (
               <div className="pbs-card">
                  <ul className="pbs-collects">
                     {stage.collects.map((item) => (
                        <li key={pickLang(item, 'es')}>
                           <FiCheck size={14} aria-hidden="true" />
                           {pickLang(item, lang)}
                        </li>
                     ))}
                  </ul>
                  <button
                     type="button"
                     className="pbs-demo-btn"
                     onClick={() => onOpenDemo(stage.demo)}
                  >
                     <FiPlay size={14} aria-hidden="true" /> {pickLang(stage.demo.label, lang)}
                  </button>
               </div>
            ) : null}

            {/* 3 — Conexión segura */}
            {stage.steps ? (
               <div className="pbs-card">
                  <ol className="pbs-secure">
                     {stage.steps.map((step, i) => (
                        <li key={pickLang(step, 'es')}>
                           <span className="pbs-secure__num">{i + 1}</span>
                           {pickLang(step, lang)}
                        </li>
                     ))}
                  </ol>
                  <p className="pbs-secure__seal">
                     <FiLock size={14} aria-hidden="true" /> {t('pbs_no_passwords')}
                  </p>
                  {stage.demo ? (
                     <button
                        type="button"
                        className="pbs-demo-btn"
                        onClick={() => onOpenDemo(stage.demo)}
                     >
                        <FiPlay size={14} aria-hidden="true" /> {pickLang(stage.demo.label, lang)}
                     </button>
                  ) : null}
               </div>
            ) : null}

            {/* 4 — Procesos activos */}
            {stage.processes ? (
               <ProcessGrid
                  processes={stage.processes}
                  lang={lang}
                  locale={locale}
                  estimateNotice={estimateNotice}
               />
            ) : null}

            {/* 5 — Ampliación */}
            {stage.groups ? (
               <div className="pbs-groups">
                  {stage.groups.map((group) => (
                     <div className="pbs-group" key={group.key}>
                        <h4>{pickLang(group.title, lang)}</h4>
                        <ul>
                           {group.items.map((item) => (
                              <li key={pickLang(item, 'es')}>{pickLang(item, lang)}</li>
                           ))}
                        </ul>
                     </div>
                  ))}
                  <p className="pbs-groups__note">{pickLang(stage.note, lang)}</p>
               </div>
            ) : null}

            {/* 6 — Retorno */}
            {stage.metrics ? (
               <div className="pbs-metrics-wrap">
                  <div className="pbs-metrics">
                     {stage.metrics.map((metric) => (
                        <div
                           className={`pbs-metric${metric.highlight ? ' is-highlight' : ''}`}
                           key={metric.key}
                        >
                           <span className="pbs-metric__value">{metric.value}</span>
                           <span className="pbs-metric__label">{pickLang(metric.label, lang)}</span>
                        </div>
                     ))}
                  </div>
                  <p className="pbs-proc__estimate">{pickLang(estimateNotice, lang)}</p>
                  {stage.cta ? (
                     <Link to={stage.cta.to} className="z-btn z-btn-3 pbs-final-cta">
                        {pickLang(stage.cta.label, lang)} <FiArrowRight size={15} />
                     </Link>
                  ) : null}
               </div>
            ) : null}
         </div>
      </div>
   );
};

export default PlaybookStage;
